import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthDialog, type AuthMode } from '../account/AuthDialog';
import { useCurrentUser } from '../account/useAuth';
import type { Project } from '../data/types';
import { capture } from '../lib/analytics';
import { browserAudioTrainer } from '../ml/browserAudioTrainer';
import { browserImageTrainer } from '../ml/browserTrainer';
import { browserPoseTrainer } from '../ml/browserPoseTrainer';
import { useProjects, useProjectsStoreApi } from '../stores/projectsContext';
import { Composer } from './Composer';
import { PublishCoachmark } from './PublishCoachmark';
import { downloadPublishBundle } from './bundle/buildPublishBundle';
import { PublishBundleError, type BundleArtifactsExtractor } from './bundle/types';
import { defaultPublishConfigFor } from './defaults';
import {
  PublishApiError,
  fetchMyPublishedApps,
  publishToBryta,
  type PublishApiResponse,
  type PublishedAppSummary,
} from './publishApi';
import { ClassQuiz } from './templates/ClassQuiz';
import { SoundReactor } from './templates/SoundReactor';
import { StickerCam } from './templates/StickerCam';
import type { PublishConfig } from './types';
import './PublishView.css';
import './templates/StickerCam.css';
import './templates/ClassQuiz.css';
import './templates/SoundReactor.css';

/**
 * Publish view — the kid lands here after training to compose the
 * "real app" version of their model. Two-pane layout: composer on
 * the left, live preview on the right. Save-on-edit so a refresh
 * never loses work.
 *
 * M1 ships the composer + preview. M2 wires the Download (zip) and
 * Publish (POST to /api/publish) buttons.
 */

export interface PublishViewProps {
  /** Override the trainer used for Download / Publish. Tests inject
   *  a fake. When omitted, production picks the right trainer per
   *  `project.kind` (image / pose / audio) at click time. */
  trainer?: BundleArtifactsExtractor;
  /** Override the download driver. Tests stub it; production uses
   *  the real `downloadPublishBundle` which builds the zip + clicks
   *  a temporary anchor element. */
  download?: typeof downloadPublishBundle;
  /** Override the publish-to-bryta.app driver. Tests stub it. */
  publish?: typeof publishToBryta;
}

function defaultTrainerFor(kind: string): BundleArtifactsExtractor {
  if (kind === 'pose') return browserPoseTrainer;
  if (kind === 'audio') return browserAudioTrainer as BundleArtifactsExtractor;
  return browserImageTrainer;
}

export function PublishView({
  trainer,
  download = downloadPublishBundle,
  publish = publishToBryta,
}: PublishViewProps = {}): JSX.Element {
  const { projectId = '' } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const store = useProjectsStoreApi();
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [published, setPublished] = useState<PublishApiResponse | null>(null);
  // Auth dialog mode — `null` means closed. We show the dialog
  // proactively if the kid clicks Publish while signed-out, AND we
  // surface a "sign in to publish" prompt above the button so they
  // don't find out about the gate by getting smacked with an error.
  const [authMode, setAuthMode] = useState<AuthMode | null>(null);
  const [previewViewport, setPreviewViewport] = useState<PreviewViewport>('desktop');
  const { data: currentUser, isLoading: userLoading } = useCurrentUser();
  const signedIn = !!currentUser;
  // Existing-publish summary for THIS project. Hydrated below once
  // `project` is in scope.
  const [existingPublish, setExistingPublish] = useState<PublishedAppSummary | null>(null);
  // Snapshot of the config that's currently live on bryta.app. Used
  // to tell whether the kid's local edits since the last publish
  // have actually shipped — the composer auto-saves locally on every
  // keystroke, but the live site only updates on Re-publish. Without
  // this signal, kids would change the title, see the preview update,
  // and assume their visitors are seeing the new version too.
  const [publishedConfigSnapshot, setPublishedConfigSnapshot] = useState<PublishConfig | null>(
    null,
  );

  // Hydrate the store on direct nav (e.g. refresh on /studio/p/.../publish).
  // Mirrors ProjectView's bootstrap: if the projects list is empty,
  // load it; either way, kick off a class + trained-model load for the
  // project in scope so the seed defaults have a class list to work
  // from.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const ensureClasses = (): void => {
      void store.getState().loadClasses(projectId);
      void store.getState().loadTrainedModel(projectId);
    };
    if (store.getState().projects.length === 0) {
      void store
        .getState()
        .load()
        .then(() => {
          if (cancelled) return;
          ensureClasses();
          setHydrated(true);
        });
    } else {
      ensureClasses();
      setHydrated(true);
    }
    return () => {
      cancelled = true;
    };
  }, [projectId, store]);

  const project = useProjects((s) => s.projects.find((p) => p.id === projectId) ?? null);
  const classes = useProjects((s) => s.classesByProject[projectId]?.byId ?? []);
  const trainedModel = useProjects((s) => s.trainedModels[projectId] ?? null);

  // Lazy-initialise the publish config the first time the kid opens
  // this view for a project. Saves immediately so a refresh sees the
  // same starting point. Wait for the class list to actually arrive
  // (otherwise we'd seed with an empty perClass map and lock that in).
  const initialised = useRef(false);
  useEffect(() => {
    if (initialised.current) return;
    if (!project || classes.length === 0) return;
    if (project.publishConfig) {
      initialised.current = true;
      return;
    }
    const seed = defaultPublishConfigFor(project.kind, classes, project.name);
    initialised.current = true;
    void store.getState().setPublishConfig(project.id, seed);
  }, [project, classes, store]);

  // Local working copy of the config so edits feel instant; flushed
  // to the store on every change. Falls back to a synthesised default
  // when the seed hasn't landed yet.
  const config = useMemo<PublishConfig | null>(() => {
    if (!project) return null;
    if (project.publishConfig) return project.publishConfig;
    return defaultPublishConfigFor(project.kind, classes, project.name);
  }, [project, classes]);

  // Fetch the kid's published apps once they're signed in + the
  // project has loaded. Lets the composer surface "Already published
  // v3" with a copy-link the moment the kid re-enters the publish
  // view, instead of showing a fresh-publish panel only on click.
  useEffect(() => {
    if (!signedIn || !project) return;
    let cancelled = false;
    void fetchMyPublishedApps()
      .then((apps) => {
        if (cancelled || !apps) return;
        const match = apps.find((a) => a.projectId === project.id) ?? null;
        setExistingPublish(match);
        // We don't get the *actual* published config back from the
        // server (the API returns version + URL only), so on first
        // hydration treat the kid's currently-saved config as the
        // baseline. Future edits during this session bump
        // config.version above the snapshot's, which is what flips
        // the "you have unpublished edits" hint on. Without this
        // baseline, the hint would either always fire (false alarm)
        // or never fire (silent staleness) depending on how we
        // initialised.
        if (match) {
          const seed = store.getState().projects.find((p) => p.id === project.id)?.publishConfig;
          if (seed) setPublishedConfigSnapshot(seed);
        }
      })
      .catch(() => {
        // Non-fatal — the panel just won't show. Kid can still publish.
      });
    return () => {
      cancelled = true;
    };
  }, [signedIn, project, store]);

  // Once a fresh publish completes, mirror it into existingPublish so
  // the "Already published" panel updates without a refetch.
  useEffect(() => {
    if (!published || !project) return;
    setExistingPublish({
      id: published.id,
      projectId: project.id,
      slug: published.slug,
      title: published.title,
      version: published.version,
      url: published.url,
      updatedAt: new Date().toISOString(),
    });
  }, [published, project]);

  const handleChange = useCallback(
    (next: PublishConfig) => {
      if (!project) return;
      void store.getState().setPublishConfig(project.id, next);
    },
    [project, store],
  );

  const handleDownload = useCallback(async (): Promise<void> => {
    if (!project || !config || downloading) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      await download({
        project: { id: project.id, name: project.name, kind: project.kind },
        classes,
        config,
        trainer: trainer ?? defaultTrainerFor(project.kind),
      });
    } catch (err) {
      const message =
        err instanceof PublishBundleError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Download failed.';
      setDownloadError(message);
    } finally {
      setDownloading(false);
    }
  }, [project, config, classes, downloading, download, trainer]);

  const handlePublish = useCallback(async (): Promise<void> => {
    if (!project || !config || publishing) return;
    // Sign-in is a precondition for publishing. Open the auth dialog
    // instead of letting the click round-trip to the backend just to
    // come back as "Not signed in" — the late error is what the kid
    // complained about.
    if (!signedIn) {
      setAuthMode('signin');
      return;
    }
    setPublishing(true);
    setPublishError(null);
    try {
      const result = await publish({
        project: { id: project.id, name: project.name, kind: project.kind },
        classes,
        config,
        trainer: trainer ?? defaultTrainerFor(project.kind),
      });
      setPublished(result);
      // Snapshot the config that just shipped so subsequent local
      // edits show up as "unsaved on the live site" rather than as
      // "fully published".
      setPublishedConfigSnapshot(config);
      // Open the live URL in a new tab so the kid sees the *real* site
      // immediately — they used to have to spot the small URL link in
      // the success panel. Only fires for fresh / re-publish actions
      // (not on hydration of an existing publish).
      if (typeof window !== 'undefined' && result.url) {
        window.open(result.url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      const message =
        err instanceof PublishBundleError || err instanceof PublishApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Publish failed.';
      setPublishError(message);
    } finally {
      setPublishing(false);
    }
  }, [project, config, classes, publishing, publish, trainer, signedIn]);

  // Bundle support now spans every project kind (image / pose /
  // audio). The trainer override is image-specific by default; the
  // production wiring at the App level resolves the right trainer
  // for each kind. Tests can inject a fake.
  const downloadSupported =
    project?.kind === 'image' || project?.kind === 'pose' || project?.kind === 'audio';
  const publishSupported = downloadSupported;

  // "Unpublished edits" — the kid edited the composer (which
  // auto-saves locally) but hasn't shipped a Re-publish, so the live
  // bryta.app site still serves the old version. Only meaningful
  // once we have a publish to compare against. `config.version`
  // monotonically increments on every composer edit, so the
  // comparison is cheap.
  const hasUnpublishedEdits =
    !!existingPublish &&
    !!publishedConfigSnapshot &&
    !!config &&
    config.version > publishedConfigSnapshot.version;

  if (!hydrated) {
    return (
      <main className="bl-publish bl-publish--centered">
        <p className="bl-publish__notice">Loading…</p>
      </main>
    );
  }

  if (!project) {
    return (
      <main className="bl-publish bl-publish--centered">
        <p className="bl-publish__notice">This project couldn&apos;t be found.</p>
        <button type="button" className="bl-publish__back" onClick={() => navigate('/studio')}>
          Back to studio
        </button>
      </main>
    );
  }

  if (!trainedModel) {
    return (
      <main className="bl-publish bl-publish--centered">
        <p className="bl-publish__notice">
          Train your model first, then come back to make it shareable.
        </p>
        <button
          type="button"
          className="bl-publish__back"
          onClick={() => navigate(`/studio/p/${project.id}`)}
        >
          Back to training
        </button>
      </main>
    );
  }

  if (!config) {
    return (
      <main className="bl-publish bl-publish--centered">
        <p className="bl-publish__notice">Loading…</p>
      </main>
    );
  }

  return (
    <main className="bl-publish">
      <PublishHeader project={project} onBack={() => navigate(`/studio/p/${project.id}`)} />

      <div className="bl-publish__columns">
        <section className="bl-publish__col bl-publish__col--composer" aria-label="Composer">
          <Composer
            config={config}
            classes={classes}
            projectKind={project.kind}
            projectName={project.name}
            onChange={handleChange}
          />
        </section>

        <section className="bl-publish__col bl-publish__col--preview" aria-label="Live preview">
          <ViewportToggle viewport={previewViewport} onChange={setPreviewViewport} />
          <div
            className={`bl-publish__preview-frame bl-publish__preview-frame--${previewViewport}`}
          >
            <div className="bl-publish__preview-canvas">
              <PreviewSwitch config={config} classes={classes} projectKind={project.kind} />
            </div>
          </div>
          {existingPublish && !published && (
            <div className="bl-publish__published bl-publish__published--prior">
              <p className="bl-publish__published-title">
                Already published — version {existingPublish.version}
                {typeof existingPublish.viewCount === 'number' && existingPublish.viewCount > 0 && (
                  <span className="bl-publish__published-views">
                    {' · '}
                    <span aria-hidden="true">👀</span>{' '}
                    {existingPublish.viewCount === 1
                      ? '1 visit'
                      : `${existingPublish.viewCount} visits`}
                  </span>
                )}
              </p>
              <a
                className="bl-publish__published-url"
                href={existingPublish.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {existingPublish.url}
              </a>
              <button
                type="button"
                className="bl-publish__published-copy"
                onClick={() => {
                  void navigator.clipboard?.writeText(existingPublish.url);
                  capture('share_link_copied', { surface: 'publish-view-existing' });
                }}
              >
                Copy link
              </button>
              {hasUnpublishedEdits ? (
                <p className="bl-publish__dirty" role="status">
                  <span aria-hidden="true">✏️</span> You have unpublished edits. Visitors still see
                  the old version — click <strong>Re-publish</strong> below to update the live site.
                </p>
              ) : (
                <p className="bl-publish__dirty bl-publish__dirty--clean" role="status">
                  <span aria-hidden="true">✅</span> The live site matches what you see here.
                </p>
              )}
            </div>
          )}
          {publishSupported && (
            <div className="bl-publish__privacy">
              <p className="bl-publish__privacy-title">What goes online when you publish</p>
              <ul className="bl-publish__privacy-list">
                <li>
                  <strong>The page</strong> — title, theme, the labels you picked. Anyone with the
                  link can open it.
                </li>
                <li>
                  <strong>Your trained model</strong> — the weights file the page needs to recognise
                  things. The viewer&apos;s browser downloads it to run predictions.
                </li>
                <li>
                  <strong>Your training photos / clips stay on this device.</strong> We never upload
                  them. The trained model is enough to make predictions without them.
                </li>
                <li>
                  Predictions on the published page run in <em>each viewer&apos;s</em> browser —
                  their camera or mic, never sent to a server.
                </li>
              </ul>
            </div>
          )}
          {!userLoading && !signedIn && publishSupported && (
            <div className="bl-publish__signin">
              <p className="bl-publish__signin-title">Sign in to publish to bryta.app</p>
              <p className="bl-publish__signin-body">
                You need an account so the page belongs to someone (and so a grown-up can review it
                later). Your project itself stays in this browser whether you sign in or not — the
                studio is local-first. Download still works without an account.
              </p>
              <div className="bl-publish__signin-actions">
                <button
                  type="button"
                  className="bl-publish__btn bl-publish__btn--secondary"
                  onClick={() => setAuthMode('signin')}
                >
                  Sign in
                </button>
                <button
                  type="button"
                  className="bl-publish__btn bl-publish__btn--primary"
                  onClick={() => setAuthMode('signup')}
                >
                  Create an account
                </button>
              </div>
            </div>
          )}
          <div className="bl-publish__deploy">
            <button
              type="button"
              className="bl-publish__btn bl-publish__btn--secondary"
              onClick={() => void handleDownload()}
              disabled={!downloadSupported || downloading}
              title={
                downloadSupported
                  ? 'Pack your page + model into a single zip you can host anywhere'
                  : 'Download is image-only for now — coming soon for this kind'
              }
            >
              {downloading ? 'Packing…' : 'Download'}
            </button>
            {/* Publish button stays visible at all times — kid sees
                the goal even when signed out. Disabled with a tooltip
                pointing at the inline sign-in prompt. Click while
                disabled is a no-op (no late "Not signed in" surprise);
                the prompt above is the path forward. */}
            <button
              type="button"
              className="bl-publish__btn bl-publish__btn--primary"
              onClick={() => void handlePublish()}
              disabled={!publishSupported || publishing || !signedIn}
              title={
                !publishSupported
                  ? 'Publish is image-only for now — coming soon for this kind'
                  : !signedIn
                    ? 'Sign in to publish to bryta.app'
                    : 'Publish to bryta.app — get a real URL you can share'
              }
            >
              {publishing
                ? 'Publishing…'
                : existingPublish
                  ? hasUnpublishedEdits
                    ? 'Re-publish edits to live site'
                    : 'Re-publish (same URL)'
                  : 'Publish to bryta.app'}
            </button>
          </div>
          {downloadError && (
            <p role="alert" className="bl-publish__deploy-error">
              {downloadError}
            </p>
          )}
          {publishError && (
            <p role="alert" className="bl-publish__deploy-error">
              {publishError}
            </p>
          )}
          {published && (
            <div className="bl-publish__published">
              <p className="bl-publish__published-title">It&apos;s live! 🎉</p>
              <a
                className="bl-publish__published-url"
                href={published.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {published.url}
              </a>
              <button
                type="button"
                className="bl-publish__published-copy"
                onClick={() => {
                  void navigator.clipboard?.writeText(published.url);
                  capture('share_link_copied', { surface: 'publish-view-fresh' });
                }}
              >
                Copy link
              </button>
            </div>
          )}
          <p className="bl-publish__deploy-hint">
            {downloadSupported
              ? 'Download a zip you can host anywhere, or publish to a real bryta.app URL.'
              : 'Live preview now. Download + publish ship next for this project kind.'}
          </p>
          <AuthDialog
            isOpen={authMode !== null}
            {...(authMode ? { initialMode: authMode } : {})}
            onClose={() => setAuthMode(null)}
          />
        </section>
      </div>
      {/* Per-template starter tip — sticky-dismissed in localStorage
          so the kid sees each template's tip exactly once. */}
      <PublishCoachmark template={config.template} />
    </main>
  );
}

type PreviewViewport =
  | 'desktop'
  | 'tablet-portrait'
  | 'tablet-landscape'
  | 'mobile-portrait'
  | 'mobile-landscape';

const VIEWPORT_PRESETS: ReadonlyArray<{
  id: PreviewViewport;
  label: string;
  icon: string;
}> = [
  { id: 'desktop', label: 'Desktop', icon: '🖥️' },
  { id: 'tablet-portrait', label: 'Tablet', icon: '📱' },
  { id: 'tablet-landscape', label: 'Tablet ↔', icon: '📱' },
  { id: 'mobile-portrait', label: 'Phone', icon: '📲' },
  { id: 'mobile-landscape', label: 'Phone ↔', icon: '📲' },
];

function ViewportToggle({
  viewport,
  onChange,
}: {
  viewport: PreviewViewport;
  onChange: (next: PreviewViewport) => void;
}): JSX.Element {
  return (
    <div className="bl-publish__viewport" role="radiogroup" aria-label="Preview viewport">
      {VIEWPORT_PRESETS.map((p) => (
        <button
          key={p.id}
          type="button"
          role="radio"
          aria-checked={viewport === p.id}
          className={`bl-publish__viewport-chip${
            viewport === p.id ? ' bl-publish__viewport-chip--active' : ''
          }`}
          onClick={() => onChange(p.id)}
        >
          <span aria-hidden="true">{p.icon}</span>
          <span>{p.label}</span>
        </button>
      ))}
    </div>
  );
}

function PublishHeader({ project, onBack }: { project: Project; onBack: () => void }): JSX.Element {
  return (
    <header className="bl-publish__header">
      <button type="button" className="bl-publish__back" onClick={onBack}>
        ← Back to training
      </button>
      <div className="bl-publish__title-block">
        <p className="bl-publish__eyebrow">Show it off</p>
        <h1 className="bl-publish__title">{project.name}</h1>
      </div>
    </header>
  );
}

function PreviewSwitch({
  config,
  classes,
  projectKind,
}: {
  config: PublishConfig;
  classes: ReadonlyArray<{ id: string; name: string }>;
  projectKind: 'image' | 'audio' | 'pose';
}): JSX.Element {
  switch (config.template) {
    case 'sticker-cam':
      return <StickerCam config={config} classes={classes} />;
    case 'class-quiz':
      return <ClassQuiz config={config} classes={classes} projectKind={projectKind} />;
    case 'sound-reactor':
      return <SoundReactor config={config} classes={classes} />;
    default:
      return <StickerCam config={config} classes={classes} />;
  }
}
